import { useState } from 'react';
import { SearchInput, ProgressLoader, ArrowRightIcon } from '@medeloop/medeloop-components-library';
import { useDispatch } from 'react-redux';
import { showToast } from 'src/store/toast';
import { semanticSearchAurora } from 'src/api/semantic-search';
import { ConceptCode } from './types';
import { formatRelevance } from './utils';
import SortableCodeTable from './sortable-code-table';

interface CodeSearchProps {
  tableName: string;
  onAddCodes: (codes: ConceptCode[]) => void;
}

export const CodeSearch: React.FC<CodeSearchProps> = ({ tableName, onAddCodes }) => {
  const dispatch = useDispatch();
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<ConceptCode[]>([]);
  const [selectedSearchCodeIds, setSelectedSearchCodeIds] = useState<string[]>([]);

  const handleSearch = async (searchTerm: string) => {
    setIsSearchLoading(true);

    const semanticSearchResults = await semanticSearchAurora(tableName, searchTerm, 100);
    if (semanticSearchResults.length === 0) {
      dispatch(
        showToast({
          type: 'dangerous',
          message: 'No results found for the given query.',
        }),
      );
    }

    const formatted: ConceptCode[] = semanticSearchResults
      .map((item) => item[1])
      .map((x) => ({
        ...x,
        relevance: formatRelevance(x.score),
        enabled: false,
        id: x.concept_id,
      }));

    setSearchResults(formatted);
    setIsSearchLoading(false);
  };

  const handleAddSelection = () => {
    const selectedCodes = searchResults
      .filter((code) => selectedSearchCodeIds.includes(code.concept_id))
      .map((code) => ({
        ...code,
        enabled: true,
        id: code.concept_id,
      }));

    onAddCodes(selectedCodes);
    setSelectedSearchCodeIds([]);
    setSearchResults([]);
  };

  return (
    <>
      <div className="pt-4 mb-6 w-full">
        <SearchInput
          placeholder="Search codes or concepts"
          onSearch={handleSearch}
          className="w-full"
          clearIcon={<div />}
          searchIcon={
            isSearchLoading ? (
              <ProgressLoader className="h-4 w-4" />
            ) : (
              <ArrowRightIcon className="object-contain aspect-square" />
            )
          }
        />
      </div>

      {searchResults.length > 0 && (
        <>
          <SortableCodeTable
            data={searchResults}
            selectedRows={selectedSearchCodeIds}
            onRowSelect={setSelectedSearchCodeIds}
            className="rounded-lg max-h-96 overflow-y-auto mb-4"
          />
          <div className="flex justify-end pt-4 pb-8">
            <button
              disabled={selectedSearchCodeIds.length === 0}
              className={`bg-cyan-400 text-black px-4 py-2 rounded-lg text-sm ${
                selectedSearchCodeIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={handleAddSelection}
            >
              Add selection
            </button>
          </div>
        </>
      )}
    </>
  );
};
